import React, { ChangeEvent, useCallback, useMemo } from 'react';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import i18n from '../../../i18n';
import { connect, useSelector } from 'react-redux';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import mergeWith from 'lodash/mergeWith';

import { useAction } from 'core/store/store';
import { useFormError } from 'core/form/form';

import {
    registrationDeliveryProvidersSelector,
    registrationSettingsDeliveryInfoSelector,
} from 'state/registration/selector';
import { RegistrationSlice } from 'state/registration/reducer';
import { makeRegistrationChangeApiShipTokenAction } from 'state/registration/actions';

import { AutocompleteControlField } from 'components/AutocompleteSelect/AutocompleteSelect';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { SwitchControlField } from 'components/SwitchControl/SwitchControl';
import { Text, TextTypography } from 'components/Text/Text';
import { UserInfo } from 'components/UserInfo/UserInfo';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { InlineQuantityControlField } from 'components/InlineQuantityControlField/InlineQuantityControlField';
import { TextControlField } from 'components/TextControl/TextControl';
import { Icon } from 'components/Icon/Icon';

import { DELIVERY_PROVIDER } from 'pages/Registration/components/DeliveryRadioButtonsField';

import { Form } from 'forms/types';
import { PaidDeliveryType } from 'types';

import { ReactComponent as HelpIcon } from 'assets/glyphs/help.svg';

import { PaidDeliveryTypeSelect } from './PaidDeliveryTypeSelect';
import { DialogNew } from '../../components/Dialog/DialogNew';
import { isUIEnabledSelector } from '../../state/app/selectors';
import { HorizontalDivider } from '../../components/HorizontalDivider/HorizontalDivider';
import { getCurrencyText } from '../../core/utils/utils';

export interface DeliveryFormData {
    enabledFreeShipping: boolean;
    minOrderPrice: number;
    freeMaxDistance: number;
    enabledPayShipping: boolean;
    price: number;
    pricePerKm: number;
    payMaxDistance: number;
    payMaxDistancePerKm: number;
    enabledServiceDelivery: boolean;
    apiShipToken: string;
    deliveryProviderKey: string;
    pricingModel: PaidDeliveryType;
    enabledSpecificDelivery: boolean;
}

const useStyles = makeStyles(theme => ({
    content: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
    },
    buttonContainer: {
        paddingBottom: theme.spacing(1),
    },
    apiShipSwitchContainer: {
        display: 'flex',
    },
    apiShipSwitch: {
        flex: 1,
    },
    apiShipHelp: {
        flexGrow: 0,
        flexBasis: 'auto',
        padding: 4,
        paddingBottom: 4,
    },
    infoContainer: {
        marginLeft: theme.spacing(6.7),
    },
    label: {
        display: 'flex',
        color: '#243143',
        textAlign: 'left',
    },
    title: {
        marginTop: theme.spacing(2),
    },
    subTitle: {
        marginTop: theme.spacing(1),
    },
    deliveryContainer: {
        display: 'flex',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down(481)]: {
            marginTop: theme.spacing(1),
        },
        [theme.breakpoints.down(375)]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            maxWidth: '77%',
        },
    },
    deliveryBlock: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: theme.spacing(3),
        [theme.breakpoints.down(480)]: {
            marginRight: theme.spacing(1.625),
        },
        [theme.breakpoints.down(375)]: {
            marginRight: 0,
        },
        '&:last-child': {
            marginRight: theme.spacing(3),
            [theme.breakpoints.down(640)]: {
                marginRight: 0,
            },
            [theme.breakpoints.down(375)]: {
                marginRight: 0,
                marginTop: theme.spacing(1),
            },
        },
    },
    line: {
        margin: theme.spacing(3, 0),
        [theme.breakpoints.down(481)]: {
            margin: theme.spacing(2, 0),
        },
    },
    userInfo: {
        marginLeft: 54,
    },
}));

const DEFAULT_DELIVERY_FORM_DATA = {
    deliveryProvider: DELIVERY_PROVIDER.SHOP,
    enabledFreeShipping: false,
    minOrderPrice: 2000,
    freeMaxDistance: 2,
    enabledPayShipping: false,
    price: 150,
    pricePerKm: 15,
    payMaxDistance: 10,
    payMaxDistancePerKm: 10,
    apiShipToken: '',
    deliveryProviderKey: null as string,
    pricingModel: PaidDeliveryType.STATIC,
    enabledSpecificDelivery: false,
};

let DeliveryFormConnected: any = reduxForm({
    form: Form.REGISTRATION_DELIVERY_FORM,
    destroyOnUnmount: false,
    enableReinitialize: true,
})((props: any) => {
    const { t } = i18n.useTranslation();
    const classes = useStyles();

    const action = useAction();

    const deliveryFormData = useSelector(registrationSettingsDeliveryInfoSelector);
    const {
        handleSubmit,
        minOrderPrice,
        price,
        pricePerKm,
        enabledFreeShipping,
        enabledPayShipping,
        enabledServiceDelivery,
        pricingModel,
        onClose,
        initialize,
        enabledSpecificDelivery,
    } = props;
    const handleClose = useCallback(() => {
        if (deliveryFormData.enabledFreeShipping === null) {
            initialize(DEFAULT_DELIVERY_FORM_DATA);
        } else {
            initialize(deliveryFormData);
        }
        onClose();
    }, [deliveryFormData, initialize, onClose]);
    const deliveryProviders = useSelector(registrationDeliveryProvidersSelector);
    const deliveryProviderOptions = useMemo(() => {
        return deliveryProviders.map(p => ({
            label: p.name,
            value: p.key,
        }));
    }, [deliveryProviders]);
    const isAnyShippingEnabled = enabledFreeShipping || enabledPayShipping || enabledSpecificDelivery;
    const validateApiShipParam = useCallback(
        (v: string) => {
            if (enabledServiceDelivery && !v) {
                return t('common:validation.required');
            }
        },
        [enabledServiceDelivery, t],
    );

    const formError = useFormError(Form.REGISTRATION_DELIVERY_FORM);

    const isUIEnabled = useSelector(isUIEnabledSelector);

    const DeliveryControls = (
        <>
            <Grid item xs={12}>
                <Text typography={TextTypography.LEAD_STRONG_CHANGE}>
                    {t('onboarding:settings.deliveryCard.payShippingDetails')}
                </Text>
            </Grid>
            <div className={classes.deliveryContainer}>
                <div className={classes.deliveryBlock}>
                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                        {t('onboarding:settings.deliveryCard.price')}
                    </Text>

                    <Field
                        name="price"
                        component={InlineQuantityControlField}
                        isDisabled={
                            !enabledPayShipping || pricingModel === PaidDeliveryType.PER_KM || enabledSpecificDelivery
                        }
                        fullWidth={true}
                        adornmentText={getCurrencyText()}
                    />
                </div>
                <div>
                    <div className={classes.deliveryBlock}>
                        <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                            {t('onboarding:settings.deliveryCard.maxDistance')}
                        </Text>

                        <Field
                            name="payMaxDistance"
                            component={InlineQuantityControlField}
                            isDisabled={
                                !enabledPayShipping ||
                                pricingModel === PaidDeliveryType.PER_KM ||
                                enabledSpecificDelivery
                            }
                            fullWidth={true}
                            adornmentText={t('common:enums.DISTANCE.KM')}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    const DeliveryPerKmControls = (
        <>
            <Grid item xs={12}>
                <Text typography={TextTypography.LEAD_STRONG_CHANGE}>
                    {t('onboarding:settings.deliveryCard.payShippingPerKmDetails')}
                </Text>
            </Grid>
            <div className={classes.deliveryContainer}>
                <div className={classes.deliveryBlock}>
                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                        {t('onboarding:settings.deliveryCard.pricePerKm')}
                    </Text>

                    <Field
                        name="pricePerKm"
                        component={InlineQuantityControlField}
                        isDisabled={
                            !enabledPayShipping || pricingModel !== PaidDeliveryType.PER_KM || enabledSpecificDelivery
                        }
                        fullWidth={true}
                        adornmentText={getCurrencyText()}
                    />
                </div>
                <div>
                    <div className={classes.deliveryBlock}>
                        <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                            {t('onboarding:settings.deliveryCard.maxDistance')}
                        </Text>

                        <Field
                            name="payMaxDistancePerKm"
                            component={InlineQuantityControlField}
                            isDisabled={
                                !enabledPayShipping ||
                                pricingModel !== PaidDeliveryType.PER_KM ||
                                enabledSpecificDelivery
                            }
                            fullWidth={true}
                            adornmentText={t('common:enums.DISTANCE.KM')}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    const payShippingText = useMemo(() => {
        const priceWithCurrency =
            pricingModel === PaidDeliveryType.PER_KM ? getCurrencyText(pricePerKm) + '/км.' : getCurrencyText(price);
        return (
            (enabledFreeShipping
                ? t('onboarding:settings:deliveryCard.freeShippingExample', {
                      minOrderPrice,
                      currency: getCurrencyText(),
                  })
                : '') +
            (enabledPayShipping
                ? t('onboarding:settings:deliveryCard.payShippingExample', {
                      priceWithCurrency,
                  })
                : '')
        );
    }, [price, pricePerKm, pricingModel, enabledFreeShipping, enabledPayShipping]);
    return (
        <ErrorBoundary>
            <DialogNew
                title={t('onboarding:settings.delivery')}
                fullHeight={true}
                onClose={handleClose}
                onSubmit={handleSubmit}
                leftButton={t('common:settings.closeButton')}
                rightButton={t('onboarding:settings:saveButton')}
                isUIEnabled={isUIEnabled}
            >
                <form onSubmit={handleSubmit} className={classes.content}>
                    <Box>
                        <Grid container>
                            <Grid item xs={12}>
                                <Field
                                    name="enabledFreeShipping"
                                    component={SwitchControlField}
                                    disabled={enabledSpecificDelivery}
                                    label={
                                        <Text typography={TextTypography.TITLE_ACCENT_CHANGE} className={classes.label}>
                                            {t('onboarding:settings.deliveryCard.freeShipping')}
                                        </Text>
                                    }
                                />
                            </Grid>
                            <div className={classes.infoContainer}>
                                <Grid item xs={12} className={classes.subTitle}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                        {t('onboarding:settings.deliveryCard.freeShippingParameters')}
                                    </Text>
                                </Grid>
                                <Grid item xs={12}>
                                    <Text typography={TextTypography.LEAD_STRONG_CHANGE} className={classes.title}>
                                        {t('onboarding:settings.deliveryCard.freeShippingDetails')}
                                    </Text>
                                </Grid>
                                <div className={classes.deliveryContainer}>
                                    <div className={classes.deliveryBlock}>
                                        <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                            {t('onboarding:settings.deliveryCard.minOrderPrice')}
                                        </Text>
                                        <Field
                                            name="minOrderPrice"
                                            component={InlineQuantityControlField}
                                            isDisabled={!enabledFreeShipping || enabledSpecificDelivery}
                                            fullWidth={true}
                                            adornmentText={getCurrencyText()}
                                        />
                                    </div>
                                    <div>
                                        <div className={classes.deliveryBlock}>
                                            <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                                {t('onboarding:settings.deliveryCard.maxDistance')}
                                            </Text>

                                            <Field
                                                name="freeMaxDistance"
                                                component={InlineQuantityControlField}
                                                isDisabled={!enabledFreeShipping || enabledSpecificDelivery}
                                                fullWidth={true}
                                                adornmentText={t('common:enums:DISTANCE:KM')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Grid>

                        <div className={classes.line}>
                            <HorizontalDivider />
                        </div>

                        <Box mt={2}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Field
                                        classes={classes.label}
                                        name="enabledPayShipping"
                                        disabled={enabledSpecificDelivery}
                                        component={SwitchControlField}
                                        label={
                                            <Text
                                                typography={TextTypography.TITLE_ACCENT_CHANGE}
                                                className={classes.label}
                                            >
                                                {t('onboarding:settings.deliveryCard.payShipping')}
                                            </Text>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <div className={classes.infoContainer}>
                                <Grid item xs={12} className={classes.subTitle}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE}>
                                        {t('onboarding:settings.deliveryCard.payShippingSubtitle')}
                                    </Text>
                                </Grid>
                                <Field
                                    name="pricingModel"
                                    component={PaidDeliveryTypeSelect}
                                    firstMethodControls={DeliveryControls}
                                    secondMethodControls={DeliveryPerKmControls}
                                    isDisabled={!enabledPayShipping || enabledSpecificDelivery}
                                />
                                <Grid container className={classes.subTitle}></Grid>
                            </div>
                        </Box>

                        {(enabledFreeShipping || enabledPayShipping) && !enabledSpecificDelivery && (
                            <>
                                <div className={classes.line}>
                                    <HorizontalDivider />
                                </div>
                                <Box className={classes.userInfo}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Text typography={TextTypography.PARA}>
                                                {t('onboarding:settings:deliveryCard.deliveryDetails')}
                                            </Text>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <UserInfo>{payShippingText}</UserInfo>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </>
                        )}
                        <div className={classes.line}>
                            <HorizontalDivider />
                        </div>
                        <Box mt={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <div className={classes.apiShipSwitchContainer}>
                                        <div className={classes.apiShipSwitch}>
                                            <Field
                                                name="enabledServiceDelivery"
                                                component={SwitchControlField}
                                                disabled={!isAnyShippingEnabled}
                                                label={
                                                    <Text
                                                        typography={TextTypography.TITLE_ACCENT_CHANGE}
                                                        className={classes.label}
                                                    >
                                                        {t('onboarding:settings.deliveryCard.serviceDelivery')}
                                                    </Text>
                                                }
                                                onChange={() => {
                                                    setTimeout(() => {
                                                        // FIXME: hack - we need both ' ' and '',
                                                        // otherwise apiShipToken validation is not working
                                                        props.change('apiShipToken', ' ');
                                                        props.change('apiShipToken', '');
                                                        props.change('deliveryProviderKey', null);
                                                    }, 100);
                                                }}
                                            />
                                        </div>
                                        <div className={classes.apiShipHelp}>
                                            <a href="/help/ApiShip.pdf" target="_blank" rel="noopener noreferrer">
                                                <Icon component={HelpIcon} svgSize={16} size={16} />
                                            </a>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                            <div className={classes.infoContainer}>
                                <Grid item xs={12}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE} className={classes.subTitle}>
                                        {t('onboarding:settings.deliveryCard.serviceSubtitleDelivery')}
                                    </Text>
                                </Grid>
                                <Grid container spacing={2} className={classes.title}>
                                    <Grid item xs={12}>
                                        <Field
                                            name="apiShipToken"
                                            component={TextControlField}
                                            label={t('onboarding:settings.deliveryCard.apiShipToken')}
                                            isDisabled={!enabledServiceDelivery || !isAnyShippingEnabled}
                                            validate={validateApiShipParam}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                action(makeRegistrationChangeApiShipTokenAction, e.target.value);
                                                props.change('deliveryProviderKey', null);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            name="deliveryProviderKey"
                                            component={AutocompleteControlField}
                                            label={t('onboarding:settings.deliveryCard.deliveryService')}
                                            isDisabled={
                                                !enabledServiceDelivery ||
                                                !isAnyShippingEnabled ||
                                                deliveryProviderOptions.length === 0
                                            }
                                            validate={validateApiShipParam}
                                            items={deliveryProviderOptions}
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                        </Box>
                        <div className={classes.line}>
                            <HorizontalDivider />
                        </div>

                        <Box mt={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Field
                                        name="enabledSpecificDelivery"
                                        component={SwitchControlField}
                                        label={
                                            <Text
                                                typography={TextTypography.TITLE_ACCENT_CHANGE}
                                                className={classes.label}
                                            >
                                                {t('onboarding:settings.deliveryCard.specificDelivery')}
                                            </Text>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <div className={classes.infoContainer}>
                                <Grid item xs={12} className={classes.subTitle}>
                                    <Text typography={TextTypography.PARA_LIGHT_CHANGE} className={classes.subTitle}>
                                        {t('onboarding:settings.deliveryCard.specificDeliveryDetails')}
                                    </Text>
                                </Grid>
                            </div>
                        </Box>

                        {formError && (
                            <Box mt={2}>
                                <Grid item xs={12}>
                                    <FormErrorMessage error={formError} />
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </form>
            </DialogNew>
        </ErrorBoundary>
    );
});

const selector = formValueSelector(Form.REGISTRATION_DELIVERY_FORM);

DeliveryFormConnected = connect((state: RegistrationSlice) => {
    const {
        enabledFreeShipping,
        minOrderPrice,
        freeMaxDistance,
        enabledPayShipping,
        price,
        pricePerKm,
        payMaxDistance,
        payMaxDistancePerKm,
        enabledServiceDelivery,
        deliveryProviderKey,
        apiShipToken,
        pricingModel,
        enabledSpecificDelivery,
    } = selector(
        state,
        'enabledFreeShipping',
        'minOrderPrice',
        'freeMaxDistance',
        'enabledPayShipping',
        'price',
        'pricePerKm',
        'payMaxDistance',
        'payMaxDistancePerKm',
        'enabledServiceDelivery',
        'deliveryProviderKey',
        'apiShipToken',
        'pricingModel',
        'enabledSpecificDelivery',
    );
    const initialValues = mergeWith({}, DEFAULT_DELIVERY_FORM_DATA, state.registration.settings.delivery, (o, s) =>
        s === null ? o : s,
    );
    return {
        enabledFreeShipping,
        minOrderPrice,
        freeMaxDistance,
        enabledPayShipping,
        price,
        pricePerKm,
        payMaxDistance,
        payMaxDistancePerKm,
        initialValues,
        enabledServiceDelivery,
        deliveryProviderKey,
        apiShipToken,
        pricingModel,
        enabledSpecificDelivery,
    };
})(DeliveryFormConnected);

export const DeliveryForm = DeliveryFormConnected;
